/**
 * Deep link URL → in-app route path.
 *
 * Route patterns must stay aligned with public/.well-known/apple-app-site-association
 * (applinks.details[].components). Universal Links and prii:// custom-scheme links
 * should resolve to the same paths.
 */

/** @typedef {'/events/*'|'/g/*'|'/groups/*'|'/profile/*'} AasaComponent */

/** Mirrors AASA components — update both files together. */
export const AASA_ROUTE_PATTERNS = [
  { component: '/events/*', regex: /^\/events\/[^/?#]+/ },
  { component: '/g/*', regex: /^\/g\/[^/?#]+/ },
  { component: '/groups/*', regex: /^\/groups\/[^/?#]+/ },
  { component: '/profile/*', regex: /^\/profile\/[^/?#]+/ },
];

/**
 * Tab pages a prii:// link may open: the lock screen card's Sets · Find · Maps buttons (Stage 6). Not in the AASA file,
 * so https links to them never open the app; only the custom scheme reaches them.
 */
export const APP_TAB_ROUTE_PATTERNS = [/^\/sets(\/[^/?#]+)?$/, /^\/find$/, /^\/map(\/[^/?#]+)?$/];

const ALLOWED_HTTP_HOSTS = new Set(['prii.app', 'www.prii.app', 'localhost', '127.0.0.1']);

/**
 * Normalize a Capacitor incoming URL (https universal link or prii:// custom scheme)
 * into a same-origin-style app path (/events/123, /g/abc, …).
 *
 * Custom-scheme convention: Capacitor/iOS parses prii://events/123 as
 * hostname="events", pathname="/123". We join host + pathname → /events/123.
 * HTTPS links use pathname as-is (hostname is prii.app, not a route segment).
 */
export function urlToAppPath(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;

  let url;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  const scheme = url.protocol.replace(':', '').toLowerCase();

  if (scheme === 'http' || scheme === 'https') {
    if (!ALLOWED_HTTP_HOSTS.has(url.hostname)) return null;
    const path = `${url.pathname}${url.search}` || '/';
    return path.startsWith('/') ? path : `/${path}`;
  }

  if (scheme === 'prii') {
    const host = url.hostname;
    const pathPart = url.pathname === '/' ? '' : url.pathname;
    const joined = host ? `/${host}${pathPart}` : url.pathname || '/';
    return `${joined}${url.search}`;
  }

  return null;
}

/**
 * Validate path against AASA route surface. Returns the path if allowed, else null.
 */
export function resolveDeepLinkPath(appPath) {
  if (!appPath || typeof appPath !== 'string' || !appPath.startsWith('/')) return null;

  const pathname = appPath.split('?')[0].split('#')[0];
  const matched =
    AASA_ROUTE_PATTERNS.some(({ regex }) => regex.test(pathname)) ||
    APP_TAB_ROUTE_PATTERNS.some((regex) => regex.test(pathname));
  if (!matched) return null;

  return appPath;
}

/** Build /login?next=… with the destination path fully encoded (including its own ?query). */
export function buildLoginRedirectPath(nextPath) {
  const params = new URLSearchParams();
  params.set('next', nextPath);
  return `/login?${params.toString()}`;
}

/** sessionStorage key — value is the raw launch URL string we already routed. */
const HANDLED_LAUNCH_URL_KEY = 'prii:deepLink:handledLaunchUrl';

/**
 * Whether this process launch URL was already routed. Uses sessionStorage so the
 * guard survives window.location reloads; only applies to getLaunchUrl(), not
 * warm appUrlOpen events.
 */
export function isLaunchUrlAlreadyHandled(rawUrl) {
  if (!rawUrl) return false;
  try {
    return sessionStorage.getItem(HANDLED_LAUNCH_URL_KEY) === rawUrl;
  } catch {
    return false;
  }
}

export function markLaunchUrlHandled(rawUrl) {
  if (!rawUrl) return;
  try {
    sessionStorage.setItem(HANDLED_LAUNCH_URL_KEY, rawUrl);
  } catch {
    // Private mode / quota — routing still proceeds; may re-route after reload.
  }
}
